import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { 
  ArrowLeft, Plus, Link as LinkIcon, Trash2, 
  Instagram, Twitter, Facebook, Globe, FileText
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Toaster, toast } from "sonner";

const SocialConnections = () => {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newConnection, setNewConnection] = useState({
    platform: "",
    profile_url: "",
    pasted_data: "",
    data_source: "manual"
  });

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const response = await authAxios.get("/social/connections");
      setConnections(response.data);
    } catch (error) {
      console.error("Error fetching connections:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!newConnection.platform) {
      toast.error("Please select a platform");
      return;
    }
    
    try {
      await authAxios.post("/social/connect", newConnection);
      toast.success("Connection added!");
      setDialogOpen(false);
      setNewConnection({ platform: "", profile_url: "", pasted_data: "", data_source: "manual" });
      fetchConnections();
    } catch (error) {
      toast.error("Failed to add connection");
    }
  };

  const handleDelete = async (connectionId) => {
    try {
      await authAxios.delete(`/social/connections/${connectionId}`);
      toast.success("Connection removed");
      fetchConnections();
    } catch (error) {
      toast.error("Failed to remove connection");
    }
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      instagram: <Instagram className="w-5 h-5" />,
      twitter: <Twitter className="w-5 h-5" />,
      facebook: <Facebook className="w-5 h-5" />,
      other: <Globe className="w-5 h-5" />
    };
    return icons[platform] || icons.other;
  };

  const getPlatformColor = (platform) => {
    const colors = {
      instagram: "bg-gradient-to-r from-purple-500 to-pink-500",
      twitter: "bg-blue-500",
      facebook: "bg-blue-600",
      other: "bg-divine-gold"
    };
    return colors[platform] || colors.other;
  };

  return (
    <div className="min-h-screen bg-divine-black">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="border-b border-white/10 bg-divine-black-light/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              onClick={() => navigate("/profile")}
              className="text-white/60 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-cinzel text-xl text-white">Social Connections</h1>
              <p className="text-white/40 text-sm">Link your accounts for deeper insights</p>
            </div>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-connection-btn"
                className="bg-divine-red hover:bg-divine-red-light text-white font-cinzel rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Connection
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-divine-black-light border-white/10 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-cinzel text-xl">Add Social Connection</DialogTitle>
              </DialogHeader>
              
              <Tabs defaultValue="url" className="mt-4">
                <TabsList className="grid w-full grid-cols-2 bg-white/5">
                  <TabsTrigger 
                    value="url" 
                    className="data-[state=active]:bg-divine-gold data-[state=active]:text-black"
                  >
                    Profile URL
                  </TabsTrigger>
                  <TabsTrigger 
                    value="paste" 
                    className="data-[state=active]:bg-divine-gold data-[state=active]:text-black"
                  >
                    Paste Data
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="url" className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm text-white/80">Platform</label>
                    <Select 
                      value={newConnection.platform} 
                      onValueChange={(v) => setNewConnection(prev => ({ ...prev, platform: v, data_source: "manual" }))}
                    >
                      <SelectTrigger data-testid="platform-select" className="mt-2 bg-white/5 border-white/10">
                        <SelectValue placeholder="Select platform" />
                      </SelectTrigger>
                      <SelectContent className="bg-divine-black-light border-white/10">
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="twitter">Twitter / X</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm text-white/80">Profile URL</label>
                    <Input
                      data-testid="profile-url-input"
                      value={newConnection.profile_url}
                      onChange={(e) => setNewConnection(prev => ({ ...prev, profile_url: e.target.value }))}
                      placeholder="https://instagram.com/username"
                      className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    />
                  </div>
                </TabsContent>
                
                <TabsContent value="paste" className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm text-white/80">Platform</label>
                    <Select 
                      value={newConnection.platform} 
                      onValueChange={(v) => setNewConnection(prev => ({ ...prev, platform: v, data_source: "paste" }))}
                    >
                      <SelectTrigger className="mt-2 bg-white/5 border-white/10">
                        <SelectValue placeholder="Select platform" />
                      </SelectTrigger>
                      <SelectContent className="bg-divine-black-light border-white/10">
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="twitter">Twitter / X</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-sm text-white/80">Paste Profile Data</label>
                    <Textarea
                      data-testid="paste-data-input"
                      value={newConnection.pasted_data}
                      onChange={(e) => setNewConnection(prev => ({ ...prev, pasted_data: e.target.value }))}
                      placeholder="Copy and paste bio, posts, or any profile information here..."
                      className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[150px]"
                    />
                  </div>
                </TabsContent>
              </Tabs>
              
              <Button
                data-testid="save-connection-btn"
                onClick={handleConnect}
                className="w-full mt-4 bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel"
              >
                Save Connection
              </Button>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Info Banner */}
        <div className="glass-gold rounded-xl p-6 mb-8">
          <h3 className="font-cinzel text-lg text-white mb-2">How Eros Gathers Insights</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start gap-3">
              <LinkIcon className="w-5 h-5 text-divine-gold flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">Profile URLs</p>
                <p className="text-white/60">Add links to public profiles</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-divine-gold flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">Paste Data</p>
                <p className="text-white/60">Copy & paste bios, posts, comments</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Globe className="w-5 h-5 text-divine-gold flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium">OAuth (Coming Soon)</p>
                <p className="text-white/60">Direct API integration</p>
              </div>
            </div>
          </div>
        </div>

        {/* Connections List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-divine-gold border-t-transparent rounded-full" />
          </div>
        ) : connections.length > 0 ? (
          <div className="space-y-4">
            {connections.map((conn) => (
              <div
                key={conn.connection_id}
                className="glass rounded-xl p-5 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full ${getPlatformColor(conn.platform)} flex items-center justify-center text-white`}>
                    {getPlatformIcon(conn.platform)}
                  </div>
                  <div>
                    <p className="text-white font-medium capitalize">{conn.platform}</p>
                    <p className="text-white/40 text-sm">
                      {conn.profile_url || `Data pasted • ${conn.pasted_data?.length || 0} characters`}
                    </p>
                  </div>
                </div>
                <Button
                  data-testid={`delete-connection-${conn.connection_id}`}
                  variant="ghost"
                  onClick={() => handleDelete(conn.connection_id)}
                  className="text-white/40 hover:text-divine-red hover:bg-divine-red/10"
                >
                  <Trash2 className="w-5 h-5" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 glass rounded-xl">
            <Globe className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="font-cinzel text-xl text-white mb-2">No Connections Yet</h3>
            <p className="text-white/40 mb-6">Add your social profiles to help Eros understand you better</p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel rounded-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Connection
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

export default SocialConnections;
